import { useState } from 'react';
import { Button, SegmentedControl } from '@ai-tutor/ui';
import { useNow } from '@/hooks/ui/useNow';
import { BreathingExercise } from '../breathing/BreathingExercise';

export type BreakActivity = 'breathe' | 'stretch' | 'eyes';

const ACTIVITY_OPTIONS = [
  { value: 'breathe', label: 'Breathe' },
  { value: 'stretch', label: 'Stretch' },
  { value: 'eyes', label: 'Rest your eyes' },
] as const;

const STRETCHES = [
  'Roll your shoulders back slowly, five times.',
  'Reach both arms up tall, then fold forward gently.',
  'Turn your head slowly to the left, then to the right.',
];

interface BreakActivitiesProps {
  activity: BreakActivity;
  onActivityChange: (activity: BreakActivity) => void;
}

/**
 * Something restful to do with the break instead of reaching for the phone:
 * moving and looking away restore attention, and scrolling mostly does not.
 */
export function BreakActivities({
  activity,
  onActivityChange,
}: BreakActivitiesProps) {
  return (
    <div className='space-y-3'>
      <SegmentedControl
        segmentClassName='tap-target'
        label='Break activity'
        size='sm'
        options={ACTIVITY_OPTIONS}
        value={activity}
        onChange={onActivityChange}
      />
      {/* No box of its own: it already sits in the panel's card. */}
      <div className='min-h-[12rem] pt-2'>
        {activity === 'breathe' && <BreathingExercise compact />}
        {activity === 'stretch' && <StretchSteps />}
        {activity === 'eyes' && <EyeRest />}
      </div>
    </div>
  );
}

function StretchSteps() {
  return (
    <div className='space-y-3'>
      <p className='text-sm font-medium text-foreground'>
        Stand up if you can. Three slow stretches:
      </p>
      <ol className='space-y-2'>
        {STRETCHES.map((step, i) => (
          <li key={step} className='flex gap-3 text-sm text-foreground'>
            <span
              className='tint-brand-strong flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-xs'
              aria-hidden='true'
            >
              {i + 1}
            </span>
            <span className='pt-0.5'>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

const EYE_REST_MS = 20_000;

function EyeRest() {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  // Stops ticking once the 20 seconds are up.
  const counting =
    startedAt !== null && Date.now() - startedAt < EYE_REST_MS + 500;
  const now = useNow(counting, 250);
  const left =
    startedAt === null ? EYE_REST_MS : EYE_REST_MS - (now - startedAt);
  const done = startedAt !== null && left <= 0;

  return (
    <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
      <p className='text-sm text-foreground'>
        Look at something at least 6 metres (20 feet) away for 20 seconds. Blink
        slowly.
      </p>
      <p
        className='font-mono text-3xl font-semibold tabular-nums text-foreground'
        aria-live='polite'
      >
        {done ? 'Done' : `${Math.ceil(Math.max(0, left) / 1000)}s`}
      </p>
      <Button
        size='sm'
        className='max-sm:h-11'
        variant='outline'
        onClick={() => setStartedAt(Date.now())}
      >
        {startedAt === null ? 'Start 20 seconds' : 'Again'}
      </Button>
    </div>
  );
}
